<template>
  <div class="employee-management-page">
    <v-progress-linear v-if="loading" indeterminate></v-progress-linear>

    <v-container class="employee-management-container">
      <header class="employee-management-header">
        <div>
          <small>Team management</small>
          <h1>Employees</h1>
          <p>Manage profiles, access, PINs, pay settings, and fingerprints.</p>
        </div>
        <v-btn v-if="employeeAccess.employee && employeeAccess.employee.create" rounded color="primary"
          @click="createEmployee"><v-icon left>mdi-account-plus-outline</v-icon>Add employee</v-btn>
      </header>
      <section class="employee-capacity">
        <div class="employee-capacity__copy">
          <span>Attendance capacity</span>
          <strong>{{ totalAttendees }} <small>of {{ employeeCountAllow || "—" }} attendees</small></strong>
        </div>
        <div class="employee-capacity__progress">
          <v-progress-linear
            v-if="subscription"
            color="primary"
            :value="percentage"
            rounded
            height="8"
          ></v-progress-linear>
          <v-skeleton-loader
            class="ma-0"
            v-else
            type="text"
          ></v-skeleton-loader>
        </div>
      </section>
      <div class="employee-management-toolbar">
        <v-text-field v-model="search" outlined dense hide-details clearable prepend-inner-icon="mdi-magnify"
          placeholder="Search employees by name, phone, or email"></v-text-field>
        <span>{{ count }} {{ count === 1 ? "employee" : "employees" }}</span>
      </div>
      <v-list v-if="employees.length" class="employee-management-list">
        <template v-for="(item, index) in employees">
          <v-list-item :key="index" class="employee-management-row" @click="!isReadOnly(item) && editEmployee(item)">
            <v-list-item-avatar>
              <v-avatar class="employee-management-avatar" size="50">
                <v-img v-if="item.imageURL" :src="item.imageURL"></v-img>
                <v-img v-else src="@/assets/images/user.jpeg"></v-img>
              </v-avatar>
            </v-list-item-avatar>

            <v-list-item-content>
              <v-list-item-title
                v-text="item.name"
                class="text-capitalize font-weight-bold"
              ></v-list-item-title>

              <v-list-item-subtitle class="employee-management-meta">
                <span v-if="item.Department" class="employee-tag">
                  <v-icon x-small>mdi-office-building-outline</v-icon>{{ item.Department.name }}</span>
                <span
                  v-if="
                    (item.metadata &&
                      item.metadata.timeClockLogin &&
                      item.metadata.timeClockLogin.admin) ||
                    (item.metadata &&
                      item.metadata.timeClockLogin &&
                      item.metadata.timeClockLogin.employee)
                  "
                >
                  <v-icon small color="warning" v-if="item.owner"
                    >mdi-crown</v-icon
                  >
                  <span
                    v-if="
                      item.metadata &&
                      item.metadata.timeClockLogin &&
                      item.metadata.timeClockLogin.admin
                    "
                    >Admin</span
                  >
                  <span
                    class="pl-1"
                    v-if="
                      item.metadata &&
                      item.metadata.timeClockLogin &&
                      item.metadata.timeClockLogin.employee
                    "
                    >Attendee</span
                  >
                  <span
                    class="pl-1"
                    v-if="
                      item.metadata &&
                      item.metadata.timeClockLogin &&
                      item.metadata.timeClockLogin.portal
                    "
                    >Portal</span
                  >
                </span>
                <span v-if="item.metadata && item.metadata.fingerprintEnrolled" class="employee-tag is-fingerprint">
                  <v-icon x-small>mdi-fingerprint</v-icon>Fingerprint
                </span>
              </v-list-item-subtitle>
              <!-- <v-list-item-subtitle>test</v-list-item-subtitle> -->
              <!-- <v-list-item-subtitle
                v-if="
                  item.metadata &&
                  item.metadata.timeClockLogin &&
                  item.metadata.timeClockLogin.employee
                "
                >Attendee</v-list-item-subtitle
              > -->
            </v-list-item-content>
            <v-list-item-action>
              <v-menu offset-y bottom left v-if="!isReadOnly(item)">
                <template v-slot:activator="{ on, attrs }">
                  <v-btn icon v-bind="attrs" v-on="on">
                    <v-icon>mdi-dots-vertical</v-icon>
                  </v-btn>
                </template>

                <v-list class="employee-action-menu">
                  <v-list-item
                    @click.stop.prevent="editEmployee(item)"
                    v-if="
                      employeeAccess.employee && employeeAccess.employee.edit
                    "
                  >
                    <v-list-item-icon><v-icon>mdi-pencil-outline</v-icon></v-list-item-icon>
                    <v-list-item-title>Edit employee</v-list-item-title>
                  </v-list-item>
                  <v-list-item
                    @click.stop.prevent="deleteEmployee(item)"
                    v-if="
                      employeeAccess.employee && employeeAccess.employee.delete
                    "
                  >
                    <v-list-item-icon><v-icon color="error">mdi-delete-outline</v-icon></v-list-item-icon>
                    <v-list-item-title class="error--text">Delete employee</v-list-item-title>
                  </v-list-item>
                  <v-divider
                    v-if="
                      employeeAccess.employee && employeeAccess.employee.edit
                    "
                  ></v-divider>
                  <v-list-item
                    @click.stop.prevent="resetFingerprint(item)"
                    v-if="
                      employeeAccess.employee && employeeAccess.employee.edit
                    "
                  >
                    <v-list-item-icon><v-icon>mdi-fingerprint-off</v-icon></v-list-item-icon>
                    <v-list-item-title>Reset fingerprint</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
            </v-list-item-action>
          </v-list-item>

        </template>
      </v-list>
      <div v-else-if="!loading" class="employee-management-empty">
        <span><v-icon>{{ search ? "mdi-account-search-outline" : "mdi-account-plus-outline" }}</v-icon></span>
        <h2>{{ search ? "No employees found" : "Add your first employee" }}</h2>
        <p>{{ search ? "Try another name, phone number, or email." : "Create an employee profile to begin tracking attendance and pay." }}</p>
        <v-btn v-if="search" text color="primary" @click="search = ''">Clear search</v-btn>
        <v-btn v-else-if="employeeAccess.employee && employeeAccess.employee.create" rounded color="primary"
          @click="createEmployee"><v-icon left>mdi-plus</v-icon>Add employee</v-btn>
      </div>
    </v-container>
    <AttachmentCreate 
      :createDialog="attachmentCreateDialog"
      @close="attachmentCreateDialog = false"
      :employeeId="EmployeeId"/>
  </div>
</template>

<script>
import employeeService from "../service";
import "vue-swipe-actions/dist/vue-swipe-actions.css";
// eslint-disable-next-line no-unused-vars
// import css from "../../src/styles/vue-swipe-actions.css";
import locationService from "@/modules/Location/service.js";
import AttachmentCreate from "@/modules/Attachment/components/Create.vue"
import { mapGetters } from "vuex";

export default {
  name: "employee-list",
  data() {
    return {
      subscription: null,
      employeeCountAllow: 0,
      totalAttendees: 0,
      search: null,
      deleteEmployees: false,
      employees: [],
      count: 0,
      limit: 50,
      offset: 0,
      loading: false,
      rowsPerPageItems: [10, 25, 50, 100],
      pagination: {
        rowsPerPage: 10,
        sortBy: "name",
      },
      enabled: true,
      page: 0,
      revealed: {},
      lastEventDescription: "",
      attachmentCreateDialog: false,
      EmployeeId: null,
    };
  },
  async created() {
    this.loadSubscription();
    this.getAll();
  },
  components: {
    AttachmentCreate
  },
  computed: {
    ...mapGetters("employee", ["employee", "employeeAccess"]),
    percentage() {
      return (100 * this.totalAttendees) / this.employeeCountAllow;
    },
  },
  // mounted() {
  //   // ideally should be in some global handler/store
  //   window.addEventListener("keydown", this.onKeyDown);
  //   window.addEventListener("keyup", this.onKeyUp);
  // },
  // beforeDestroy() {
  //   window.removeEventListener("keydown", this.onKeyDown);
  //   window.removeEventListener("keyup", this.onKeyUp);
  // },
  watch: {
    search() {
      if (!this.search || this.search.length > 2) {
        this.getAll();
      }
    },
  },
  sockets: {
    employeeStatusChange() {
      this.getAll();
    },
  },
  methods: {
    resetFingerprint(employee) {
      this.$swal
        .fire({
          title: "Are you sure?",
          text: "This will permanently remove the fingerprint. It cannot be undone.",
          icon: "warning",
          showCancelButton: true,
          confirmButtonColor: "#3085d6",
          cancelButtonColor: "#d33",
          confirmButtonText: "Yes, reset it",
        })
        .then((result) => {
          if (!result.isConfirmed) return;

          // clone employee to avoid Vue reactivity issues
          const updated = { ...employee };

          // make sure metadata exists
          updated.metadata = { ...employee.metadata };

          // remove / reset fingerprint fields
          updated.metadata.fingerprintEnrolled = false;
          updated.metadata.fingerprintTemplate = null;
          updated.metadata.fingerprintTemplates = [];
          updated.metadata.fingerprintTries = 0;

          // send update using your existing update() call
          employeeService
            .update(employee.id, updated)
            .then(() => {
              this.$toast.success("Fingerprint has been reset", {
                timeout: 2000,
              });
              this.getAll();
            })
            .catch((err) => {
              this.$toast.error(err.data?.message || "Error", {
                timeout: 2000,
              });
            });
        });
    },
    isReadOnly(selectedEmployee) {
      if (selectedEmployee.owner && this.employee.id != selectedEmployee.id)
        return true;
      else return false;
    },
    createEmployee() {
      this.$router.push({ name: "employee-create" });
    },
    loadSubscription() {
      const location = JSON.parse(
        localStorage.getItem(`${process.env.VUE_APP_STORAGE_PREFIX}location`)
      );
      // // console.log("location", location);
      if (location) {
        return locationService
          .getSubscription(location.id)
          .then(async (response) => {
            // console.log("sub", response.data);
            this.subscription = response.data[0];
            if (this.subscription) {
              let total = 0;
              this.subscription.items.data.map((row) => {
                if (row.price.metadata && row.price.metadata.employeeCount) {
                  // console.log("row", row.price.metadata);
                  total +=
                    parseInt(row.price.metadata.employeeCount) * row.quantity;
                }
              });
              // // console.log("total ", total);
              if (total !== 0) {
                this.employeeCountAllow = total;
              }
              if (
                this.subscription.metadata &&
                this.subscription.metadata.allowEmployee
              ) {
                this.employeeCountAllow =
                  this.subscription.metadata.allowEmployee;
              }
            }
          })
          .catch((error) => {
            console.log(error);
          });
      }
    },
    remove(item) {
      this.$set(
        this.mockSwipeList,
        this.page,
        this.mockSwipeList[this.page].filter((i) => i !== item)
      );
    },
    setLastEvent(name, { item, index }) {
      this.lastEventDescription = {
        name,
        index,
        id: item.id,
      };
    },
    getAll() {
      let noCache = true;
      this.loading = true;
      return employeeService
        .getAll(
          {
            limit: this.limit,
            offset: this.offset,
            local: true,
            noPagination: true,
            search: this.search,
            orderBy: [this.pagination.sortBy],
          },
          noCache
        )
        .then(async (response) => {
          this.totalAttendees = 0;
          console.log("response", response);
          const employees = response.data;
          if (this.employeeAccess.onlyView) {
            // console.log('test test')

            await Promise.all(
              employees.map((e) => {
                if (
                  e.metadata &&
                  e.metadata.timeClockLogin &&
                  e.metadata.timeClockLogin.employee
                ) {
                  this.totalAttendees++;
                }
                this.employeeAccess.onlyViewEmployee.map((emp) => {
                  if (emp === e.id) {
                    this.employees.push(e);
                  }
                });
              })
            );
          } else {
            await Promise.all(
              employees.map((e) => {
                if (
                  e.metadata &&
                  e.metadata.timeClockLogin &&
                  e.metadata.timeClockLogin.employee
                ) {
                  this.totalAttendees++;
                }
                // this.employeeAccess.onlyViewEmployee.map(emp => {
                //     if (emp === e.id) {
                //       this.employees.push(e);
                //     }
                //   })
              })
            );
            this.employees = employees.sort((a, b) => {
              if (a.name < b.name) {
                return -1;
              }
              if (a.name > b.name) {
                return 1;
              }
              return 0;
            });
          }

          // this.employees = response.data;

          // console.log('response.data is :', this.employees)
          this.count = response.count;
          // if (this.employees) {
          //   this.employees.map((e) => {
          //     if (
          //       e.metadata &&
          //       e.metadata.timeClockLogin &&
          //       e.metadata.timeClockLogin.employee
          //     ) {
          //       this.totalAttendees++;
          //     }
          //     // this.totalAttendees
          //   });
          // }
          this.loading = false;
          return response;
        })
        .catch((error) => {
          this.loading = false;
          throw error;
        });
    },
    deleteEmployee(employee) {
      return this.$swal
        .fire({
          title: "Are you sure?",
          text: "You won't be able to revert this!",
          icon: "warning",
          showCancelButton: true,
          confirmButtonColor: "#3085d6",
          cancelButtonColor: "#d33",
          confirmButtonText: "Yes",
          cancelButtonText: "No",
        })
        .then((result) => {
          if (result.isConfirmed) {
            employeeService.delete(employee.id).then((response) => {
              if (response.data) {
                this.$toast.success("Employee has been deleted", {
                  timeout: 2000,
                });

                this.getAll();
              }
            });
          }
        })
        .catch((error) => {
          this.$toast.error(error.data ? error.data.message : "Error", {
            timeout: 2000,
          });
        });
    },
    editEmployee(employee) {
      if (employee) {
        return this.$router.push({
          name: "employee-edit",
          params: { id: employee.id, fromList: true },
        });
      }
      return alert("Id Not Found");
    },
  },
};
</script>

<style scoped>
.employee-management-page {
  color: var(--v-textPrimary-base);
}

.employee-management-container {
  height: calc(100vh - 132px);
  max-width: 1050px;
  overflow: auto;
  padding: 24px 20px 100px;
}

.employee-management-header {
  align-items: center;
  display: flex;
  gap: 16px;
  justify-content: space-between;
  margin-bottom: 16px;
}

.employee-management-header small {
  color: var(--v-textSecondary-base);
  font-size: 0.65rem;
  font-weight: 800;
  letter-spacing: 0.1em;
  text-transform: uppercase;
}

.employee-management-header h1 {
  font-size: 1.7rem;
  margin: 1px 0 3px;
}

.employee-management-header p {
  color: var(--v-textSecondary-base);
  font-size: 0.78rem;
  margin: 0;
}

.employee-capacity,
.employee-management-toolbar {
  align-items: center;
  background: var(--v-surface-base);
  border: 1px solid var(--v-border-base);
  border-radius: 15px;
  display: flex;
}

.employee-capacity {
  gap: 20px;
  margin-bottom: 10px;
  padding: 13px 16px;
}

.employee-capacity__copy span,
.employee-capacity__copy strong {
  display: block;
}

.employee-capacity__copy span {
  color: var(--v-textSecondary-base);
  font-size: 0.67rem;
  font-weight: 700;
}

.employee-capacity__copy strong {
  font-size: 1.15rem;
}

.employee-capacity__copy small {
  color: var(--v-textSecondary-base);
  font-size: 0.7rem;
}

.employee-capacity__progress {
  flex: 1;
}

.employee-management-toolbar {
  gap: 12px;
  margin-bottom: 12px;
  padding: 9px;
}

.employee-management-toolbar ::v-deep .v-input__slot {
  background: var(--v-surfaceMuted-base) !important;
}

.employee-management-toolbar > span {
  color: var(--v-textSecondary-base);
  flex: 0 0 auto;
  font-size: 0.72rem;
  font-weight: 700;
  padding-right: 8px;
}

.employee-management-list {
  background: transparent !important;
  padding: 0;
}

.employee-management-row {
  background: var(--v-surface-base);
  border: 1px solid var(--v-border-base);
  border-radius: 14px;
  cursor: pointer;
  margin-bottom: 9px;
  min-height: 82px;
}

.employee-management-avatar {
  border: 2px solid var(--v-border-base);
}

.employee-management-meta {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
  margin-top: 5px;
}

.employee-tag {
  align-items: center;
  background: var(--v-surfaceMuted-base);
  border: 1px solid var(--v-border-base);
  border-radius: 999px;
  display: inline-flex;
  font-size: 0.65rem;
  gap: 3px;
  padding: 2px 7px;
}

.employee-tag .v-icon {
  color: var(--v-textSecondary-base);
}

.employee-tag.is-fingerprint {
  color: var(--v-success-base);
}

.employee-tag.is-fingerprint .v-icon {
  color: inherit;
}

.employee-management-empty {
  align-items: center;
  background: var(--v-surface-base);
  border: 1px dashed var(--v-border-base);
  border-radius: 18px;
  display: flex;
  flex-direction: column;
  min-height: 300px;
  padding: 48px 20px;
  text-align: center;
}

.employee-management-empty > span {
  align-items: center;
  background: var(--v-surfaceMuted-base);
  border-radius: 17px;
  display: flex;
  height: 60px;
  justify-content: center;
  width: 60px;
}

.employee-management-empty .v-icon {
  color: var(--v-primary-base);
  font-size: 30px;
}

.employee-management-empty h2 {
  font-size: 1.15rem;
  margin-top: 14px;
}

.employee-management-empty p {
  color: var(--v-textSecondary-base);
  font-size: 0.78rem;
  margin: 5px auto 18px;
}

@media (max-width: 600px) {
  .employee-management-container {
    height: calc(100vh - 122px);
    padding: 16px 12px 90px;
  }

  .employee-management-header {
    align-items: flex-start;
  }

  .employee-management-header h1 {
    font-size: 1.35rem;
  }

  .employee-management-header p,
  .employee-management-toolbar > span {
    display: none;
  }

  .employee-management-header .v-btn {
    min-width: 44px !important;
    padding: 0 12px !important;
  }

  .employee-capacity {
    gap: 12px;
  }

  .employee-management-row {
    padding: 0 8px;
  }
}
</style>
